import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { apiService, type Classroom } from "@/services/api.service";
import { TargetSearch } from "./TargetSearch";
import { TableSkeleton } from "./FlagStoreSkeletons";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { flagStore, type Explained, type FlagAuditEvent, type FlagScope, type StoredValue } from "@/services/flag-store.service";
import { SCOPE_LABELS, formatStoredValue } from "@/lib/flag-store";

export type PendingChange = {
  flagKey: string;
  scope: FlagScope;
  scopeId: string | null;
  targetLabel: string;
  current: StoredValue | null;
  next: StoredValue | null;
  expectedVersion: number | null;
  reason: string;
};

export function ConfirmChangeDialog({ change, environment, saving, onCancel, onConfirm }: {
  change: PendingChange | null;
  environment: string;
  saving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return <Dialog open={change !== null} onOpenChange={open => { if (!open && !saving) onCancel(); }}>
    {change && <ConfirmChangeContent key={`${change.scope}-${change.scopeId}-${String(change.next)}`} change={change} environment={environment} saving={saving} onCancel={onCancel} onConfirm={onConfirm} />}
  </Dialog>;
}

function ConfirmChangeContent({ change, environment, saving, onCancel, onConfirm }: {
  change: PendingChange;
  environment: string;
  saving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const [affected, setAffected] = useState<number | null>(null);
  const [countError, setCountError] = useState("");

  useEffect(() => {
    let active = true;
    flagStore.blastRadius(change.scope, change.scopeId)
      .then(result => { if (active) setAffected(result.affected_users); })
      .catch(() => { if (active) setCountError("Could not count affected users."); });
    return () => { active = false; };
  }, [change]);

  const prod = environment === "prod";
  return <DialogContent>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          Confirm flag change <Badge variant={prod ? "destructive" : "outline"}>{environment.toUpperCase()}</Badge>
        </DialogTitle>
        <DialogDescription>
          {change.next === null ? "Remove the override for" : "Set"} <strong>{change.flagKey}</strong>
          {change.next !== null && <> to <strong>{formatStoredValue(change.next)}</strong></>} for {change.targetLabel}.
          {" "}Current value at this scope: {formatStoredValue(change.current)}.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-1 text-sm">
        <p role="status">
          {countError || (affected === null ? "Counting affected users…" : <>Affects about <strong>{affected}</strong> {affected === 1 ? "user" : "users"}, unless a narrower scope overrides it.</>)}
        </p>
        <p className="text-muted-foreground">Reason: {change.reason}</p>
        {prod && <p className="font-medium text-destructive">This changes production.</p>}
      </div>
      <DialogFooter>
        <Button variant="outline" disabled={saving} onClick={onCancel}>Cancel</Button>
        <Button variant={prod ? "destructive" : "default"} disabled={saving || affected === null && !countError} onClick={onConfirm}>
          {saving ? "Saving…" : "Confirm change"}
        </Button>
      </DialogFooter>
    </DialogContent>;
}

export function HistoryPanel({ flagKey, targetName }: { flagKey: string; targetName: (scope: FlagScope, id: string | null) => string }) {
  const [events, setEvents] = useState<FlagAuditEvent[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    flagStore.audit(flagKey)
      .then(page => { if (active) { setEvents(page.events); setCursor(page.next_cursor); } })
      .catch(() => { if (active) setError("Could not load history."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [flagKey]);

  async function loadMore() {
    if (!cursor) return;
    setLoading(true);
    try {
      const page = await flagStore.audit(flagKey, cursor);
      setEvents(current => [...current, ...page.events]);
      setCursor(page.next_cursor);
    } catch {
      setError("Could not load more history.");
    } finally {
      setLoading(false);
    }
  }

  if (error) return <p role="alert" className="text-sm text-destructive">{error}</p>;
  if (loading && events.length === 0) return <TableSkeleton label="Loading history" />;
  if (!loading && events.length === 0) return <p className="text-sm text-muted-foreground">No changes yet.</p>;
  return <div className="space-y-2">
    <table className="w-full text-sm">
      <thead className="text-left text-muted-foreground"><tr><th className="py-1 pr-3">When</th><th className="pr-3">For</th><th className="pr-3">Change</th><th className="pr-3">By</th><th>Reason</th></tr></thead>
      <tbody>
        {events.map(event => <tr key={`${event.at}-${event.scope}-${event.scope_id}`} className="border-t align-top">
          <td className="py-1 pr-2 whitespace-nowrap">{new Date(event.at).toLocaleString()}</td>
          <td className="pr-2">{targetName(event.scope, event.scope_id)}</td>
          <td className="pr-2">{formatStoredValue(event.old_value)} → {event.action === "CLEAR" ? "Inherit" : formatStoredValue(event.new_value)}</td>
          <td className="pr-2">{event.actor?.email}</td>
          <td>{event.reason}</td>
        </tr>)}
      </tbody>
    </table>
    {loading && <TableSkeleton rows={2} label="Loading more history" />}
    {cursor && !loading && <Button variant="outline" size="sm" onClick={loadMore}>Load more</Button>}
  </div>;
}

export function ExplainUserTab() {
  const [organizations, setOrganizations] = useState<{ id: string; name: string }[]>([]);
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);

  useEffect(() => {
    let active = true;
    Promise.all([apiService.getOrganizations(), apiService.getClassrooms()])
      .then(([orgs, rooms]) => { if (active) { setOrganizations(orgs); setClassrooms(rooms); } })
      .catch(() => { if (active) toast.error("Organization and classroom names could not be loaded"); });
    return () => { active = false; };
  }, []);

  const targetName = (scope: FlagScope, id: string | null) => {
    const name = scope === "org" ? organizations.find(org => org.id === id)?.name
      : scope === "classroom" ? classrooms.find(room => String(room.id) === id)?.name
      : null;
    return `${SCOPE_LABELS[scope].toLowerCase()} ${name ? `${name} (${id})` : id}`;
  };

  return <Card className="p-4"><ExplainPanel targetName={targetName} organizations={organizations} classrooms={classrooms} /></Card>;
}

function ExplainPanel({ targetName, organizations, classrooms }: {
  targetName: (scope: FlagScope, id: string | null) => string;
  organizations: { id: string; name: string }[];
  classrooms: Classroom[];
}) {
  const [userId, setUserId] = useState("");
  const [userLabel, setUserLabel] = useState("");
  const [classroomId, setClassroomId] = useState("");
  const [classroomLabel, setClassroomLabel] = useState("");
  const [result, setResult] = useState<Explained | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const target = useRef(0);
  const validUser = /^[1-9][0-9]*$/.test(userId);

  function changeTarget(apply: () => void) {
    target.current += 1;
    setResult(null);
    setError("");
    apply();
  }
  const validClassroom = classroomId === "" || /^[1-9][0-9]*$/.test(classroomId);

  async function explain() {
    const requested = target.current;
    setLoading(true);
    setError("");
    try {
      const explained = await flagStore.explain(Number(userId), classroomId ? Number(classroomId) : undefined);
      if (requested === target.current) setResult(explained);
    } catch (caught) {
      if (requested !== target.current) return;
      setResult(null);
      setError(caught instanceof Error ? caught.message : "Could not evaluate flags.");
    } finally {
      setLoading(false);
    }
  }

  return <div className="space-y-3">
    <p className="text-sm text-muted-foreground">Shows every flag's value for one user and the scope it came from. Leave the classroom empty to use the user's own classroom.</p>
    <div className="flex flex-wrap items-end gap-2">
      <div className="flex flex-col gap-1 text-xs font-medium">
        <span>User</span>
        <TargetSearch scope="user" organizations={organizations} classrooms={classrooms} selectedLabel={userLabel}
          onSelect={(id, name) => changeTarget(() => { setUserId(id); setUserLabel(`${name} (${id})`); })} />
      </div>
      <div className="flex flex-col gap-1 text-xs font-medium">
        <span>Classroom (optional)</span>
        <div className="flex items-center gap-1">
          <TargetSearch scope="classroom" organizations={organizations} classrooms={classrooms} selectedLabel={classroomLabel}
            onSelect={(id, name) => changeTarget(() => { setClassroomId(id); setClassroomLabel(`${name} (#${id})`); })} />
          {classroomId && <Button size="sm" variant="ghost" aria-label="Clear classroom" onClick={() => changeTarget(() => { setClassroomId(""); setClassroomLabel(""); })}>Clear</Button>}
        </div>
      </div>
      <Button size="sm" disabled={!validUser || !validClassroom || loading} onClick={explain}>{loading ? "Evaluating…" : "Explain"}</Button>
    </div>
    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
    {loading && <TableSkeleton rows={8} label="Evaluating flags" />}
    {result && !loading && <p className="text-sm">
      Evaluated as {userLabel || `user ${result.context.user_id}`}
      {result.context.classroom_id ? `, ${targetName("classroom", String(result.context.classroom_id))}` : ", no single classroom"}
      {result.context.org_id ? `, ${targetName("org", result.context.org_id)}` : ", no organization"}.
    </p>}
    {result && !loading && <table className="w-full text-sm">
      <thead className="text-left text-muted-foreground"><tr><th className="py-1 pr-3">Flag</th><th className="pr-3">Value</th><th>From</th></tr></thead>
      <tbody>
        {Object.entries(result.flags).map(([key, item]) => <tr key={key} className="border-t">
          <td className="py-1 pr-2 font-mono text-xs">{key}</td>
          <td className="pr-2">{formatStoredValue(item.value)}</td>
          <td>{item.source === "default" ? "Default" : SCOPE_LABELS[item.source]}</td>
        </tr>)}
      </tbody>
    </table>}
  </div>;
}
